import { Check, X, Pencil, Loader2, MessageSquare, Puzzle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { QRPendingOrder } from "@/types/qr-orders";
import { formatRelative } from "@/lib/formatRelative";
import { qrTableLabel } from "@/lib/qrAccept";

interface QRPendingOrderCardProps {
  order: QRPendingOrder;
  onAccept: (order: QRPendingOrder) => void;
  onModify: (order: QRPendingOrder) => void;
  onReject: (order: QRPendingOrder) => void;
  isProcessing: boolean;
}

export function QRPendingOrderCard({
  order,
  onAccept,
  onModify,
  onReject,
  isProcessing,
}: QRPendingOrderCardProps) {
  const totalItems = order.items.reduce((s, i) => s + i.quantity, 0);
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">{qrTableLabel(order.table_number)}</Badge>
            <span className="text-xs text-muted-foreground">{formatRelative(order.submitted_at)}</span>
          </div>
          <p className="text-sm font-semibold mt-1">{order.customer_name ?? "Walk-in"}</p>
        </div>
        <span className="text-xs text-muted-foreground">{totalItems} item{totalItems === 1 ? "" : "s"}</span>
      </div>

      {order.auto_accept_error && (
        <p className="text-xs text-[hsl(var(--status-billing-ink))]">Couldn't auto-accept: {order.auto_accept_error}</p>
      )}

      <div className="space-y-1.5 text-sm">
        {order.items.map((item, idx) => {
          // Under the name: the variant and add-ons, then the guest's note for the kitchen.
          const extras = [
            item.variant_name,
            ...(item.addons ?? []).map((a) =>
              a.addon_name ? (a.quantity > 1 ? `${a.addon_name} ×${a.quantity}` : a.addon_name) : null,
            ),
          ].filter(Boolean);
          const note = item.special_instructions?.trim();
          return (
            <div key={idx} data-qr-pending-line className="min-w-0">
              <div className="break-words">
                {item.quantity} × {item.item_name ?? item.item_id}
              </div>
              {extras.length > 0 && (
                <div data-qr-line-extras className="flex items-start gap-1 text-xs text-muted-foreground">
                  <Puzzle aria-hidden className="mt-0.5 h-3 w-3 shrink-0" />
                  <span className="min-w-0 break-words">{extras.join(" · ")}</span>
                </div>
              )}
              {note && (
                <div
                  data-qr-line-note
                  className="flex items-start gap-1 text-xs italic text-[hsl(var(--status-active-kot-ink))]"
                >
                  <MessageSquare aria-hidden className="mt-0.5 h-3 w-3 shrink-0" />
                  <span className="min-w-0 break-words">{note}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 pt-2 border-t border-border">
        <Button
          size="sm"
          className="flex-1 gap-1"
          onClick={() => onAccept(order)}
          disabled={isProcessing}
        >
          {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Accept
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() => onModify(order)}
          disabled={isProcessing}
        >
          <Pencil className="w-3 h-3" />
          Modify
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1 text-destructive hover:text-destructive"
          onClick={() => onReject(order)}
          disabled={isProcessing}
        >
          <X className="w-3 h-3" />
          Reject
        </Button>
      </div>
    </div>
  );
}
